/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import React, {PureComponent} from 'react';
import moment, {Moment} from 'moment';
import {ControlVariant} from "../../util/interfaces";
import DateField from "./dateField";
import {DefaultInputProps, DVRInputControllerProps} from "../v2/inputController_v2";
import {toMoment} from "../../util/momentUtil";

export enum DateType {DATETIME, DATE, TIME}

export enum DateReturnType {DATE, MOMENT, STRING}

interface Props<T extends string | Moment | Date = string> extends Omit<DVRInputControllerProps, 'value' | 'onChange'> {
    onChange: (value: T) => void;
    dateType: DateType;
    disableFuture: boolean;
    disablePast: boolean;
    disableAfter?: T;
    disableBefore?: T;
    value?: T;
    returnType: DateReturnType;
    pickerClass: string;
}

interface State {
    value: string;
}

export interface DateTimeParts {
    day: string;
    month: string;
    year: string;
    hour: string;
    minute: string;
}

export default class DateFieldController<T extends string | Moment | Date> extends PureComponent<Props<T>, State> {
    static defaultProps = Object.assign(DefaultInputProps, {
        dateType: DateType.DATE,
        disableFuture: false,
        disablePast: false,
        returnType: DateReturnType.STRING,
        variant: ControlVariant.SIMPLE,
        clearAble: true,
        disabled: false,
        readOnly: false,
        fullWidth: false,
        multiLine: false,
        error: null,
        label: null,
        className: '',
        containerClass: '',
        inputContainerClass: '',
        inputClass: '',
        labelClass: '',
        errorClass: '',
        pickerClass: '',
        ornaments: [],
        rows: 3,
        autoFocus: false,
        type: 'text',
        step: 1,
        inputProps: {},
        unControlled: false,
        autoSelect: false,
    });

    constructor(props: Props<T>) {
        super(props);
        this.state = {
            value: this.getInitValue(props),
        };
    }

    private getInitValue = (props: Props<T>): string => {
        let momentValue: Moment;
        const {value, dateType} = props;
        if (!value || (typeof value === 'string' && !value.length)) {
            return '';
        }
        if (value) {
            if (moment.isMoment(value)) momentValue = value;
            else {
                if (value instanceof Date) momentValue = moment(value);
                else {
                    momentValue = toMoment(value);
                }
            }
        } else momentValue = moment();
        let format: string;
        if (dateType === DateType.DATE) format = 'DD-MM-YYYY';
        else if (dateType === DateType.DATETIME) format = 'DD-MM-YYYY HH:mm';
        else format = 'HH:mm';
        return momentValue.format(format);
    };

    onKeyDown = (evt: React.KeyboardEvent) => {
        if (/\d/.test(evt.key)) {
            const target = evt.target as HTMLInputElement, charPos = target.selectionStart, currentValue = target.value;
            if (charPos !== null) {
                let nextValue: string;
                if (charPos === 0) {
                    nextValue = evt.key + currentValue.substr(1);
                } else if (charPos === currentValue.length - 1) {
                    nextValue = currentValue.substr(0, currentValue.length - 1) + evt.key;
                } else nextValue =
                    currentValue.substr(0, charPos) + evt.key + currentValue.substr(charPos + 1, currentValue.length);
                this.onChangeText(nextValue, evt);
            }
        }
        if (this.props.onKeyDown) this.props.onKeyDown(evt);
    };

    onChangeText = (value: string, evt?: React.ChangeEvent | React.KeyboardEvent, callback?: Function) => {
        if (!/\d/.test(value)) {
            this.onChange('');
            this.setState({value: ''}, () => {
                if (callback) callback();
            });
        } else {
            let target: HTMLInputElement | null = null, currentCaret: number | null = null;
            if (evt) {
                target = evt.target as HTMLInputElement;
                currentCaret = target.selectionStart;
            }
            let mask: string = this.getMask(), newValue = mask,
                valueChars = value.replace(/[-:a-z ]/g, '').split('');
            if (valueChars.length) {
                for (let i = 0; i < mask.length; i++) {
                    if (/[-: ]/.test(mask.charAt(i))) continue;
                    newValue = newValue.substr(0, i) + valueChars[0] + newValue.substr(i + 1, newValue.length);
                    valueChars = valueChars.slice(Math.min(1, valueChars.length), valueChars.length);
                    if (!valueChars.length) break;
                }
            }
            this.onChange(newValue);
            this.setState({value: newValue}, () => {
                if (callback) callback();
                else if (target && currentCaret !== null) {
                    // Skip mask chars
                    currentCaret++;
                    if (/[-: ]/.test(newValue.charAt(currentCaret))) currentCaret++;
                    window.setTimeout(() => {
                        if (target) {
                            target.selectionStart = currentCaret;
                            target.selectionEnd = currentCaret;
                        }
                    });
                }
            });
        }
    };

    onChange = (value: string) => {
        const {returnType, onChange, dateType} = this.props;
        this.setState({value});
        // Only call onChange when the date is validly (only containing numbers)
        if (!/[a-zA-Z]/.test(value)) {
            let momentValue: Moment;
            if (dateType === DateType.DATE) momentValue = toMoment(value, 'DD-MM-YYYY');
            else if (dateType === DateType.TIME) momentValue = toMoment(value, 'HH:mm');
            else momentValue = toMoment(value, 'DD-MM-YYYY HH:mm');
            switch (returnType) {
                case DateReturnType.STRING:
                    switch (dateType) {
                        case DateType.TIME:
                            onChange(momentValue.format('HH:mm') as T);
                            break;
                        case DateType.DATETIME:
                            onChange(momentValue.toISOString(true) as T);
                            break;
                        case DateType.DATE:
                            onChange(momentValue.format('YYYY-MM-DD') as T);
                            break;
                    }
                    break;
                case DateReturnType.DATE:
                    onChange(momentValue.toDate() as T);
                    break;
                case DateReturnType.MOMENT:
                    onChange(momentValue as T);
                    break;
            }
        }
    };

    getMask = (): string => {
        const {dateType} = this.props;
        switch (dateType) {
            case DateType.TIME:
                return 'uu:mm';
            case DateType.DATETIME:
                return 'dd-mm-yyyy uu:mm';
            case DateType.DATE:
                return 'dd-mm-yyyy';
        }
    };

    getDateTimeParts = (): DateTimeParts => {
        const {value} = this.state, {dateType} = this.props, dateTimeParts: DateTimeParts = {
            day: 'dd',
            hour: 'hh',
            minute: 'mm',
            month: 'mm',
            year: 'yyyy',
        };
        if (value && value.length) {
            let dateParts: string[], timeParts: string[];
            switch (dateType) {
                case DateType.DATE:
                    dateParts = value.split('-');
                    timeParts = [];
                    break;
                case DateType.DATETIME:
                    const date = value.split(' ')[0], time = value.split(' ')[1];
                    dateParts = date.split('-');
                    timeParts = time.split(':');
                    break;
                case DateType.TIME:
                    timeParts = value.split(':');
                    dateParts = [];
                    break;
            }
            if (dateParts.length) {
                dateTimeParts.day = dateParts[0];
                dateTimeParts.month = dateParts[1];
                dateTimeParts.year = dateParts[2];
            }
            if (timeParts.length) {
                dateTimeParts.hour = timeParts[0];
                dateTimeParts.minute = timeParts[1];
            }
        }
        return dateTimeParts;
    };

    render = () => {
        const {value} = this.state;
        return <DateField dateParts={this.getDateTimeParts()} {...this.props} value={value} onChange={this.onChange}
                          onChangeText={this.onChangeText} mask={this.getMask()} onKeyDown={this.onKeyDown}/>
    };
}
